import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Star, DotsSixVertical, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = [
  { id: "Potential", label: "Potential", color: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  { id: "Engaged", label: "Engaged", color: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  { id: "Negotiation", label: "Negotiation", color: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-500" },
  { id: "Converted", label: "Converted", color: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  { id: "Lost", label: "Lost", color: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
];

function CustomerCard({ customer, onDragStart, dragging }) {
  const navigate = useNavigate();
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, customer)}
      onClick={() => navigate(`/sales/customers/${customer.id}`)}
      className={`bg-white border border-slate-200 rounded-md p-3 cursor-grab active:cursor-grabbing hover:shadow-sm hover:border-slate-300 transition-all ${
        dragging ? "opacity-40" : ""
      }`}
      data-testid={`kanban-card-${customer.id}`}
    >
      <div className="flex items-start gap-2">
        <DotsSixVertical size={14} className="text-slate-300 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-slate-900 truncate">
            {customer.name}
          </div>
          {customer.company && (
            <div className="text-xs text-slate-500 truncate">
              {customer.company}
            </div>
          )}
          {(() => {
            const pocs = customer.svraf_pocs || [];
            const primary = pocs.find((p) => p.is_primary) || pocs[0];
            if (!primary) return null;
            return (
              <div className="mt-2 flex items-center gap-1 text-xs text-slate-600">
                <Star size={10} weight="fill" className="text-amber-500" />
                <span className="truncate">{primary.name}</span>
              </div>
            );
          })()}
          {customer.opportunity_value > 0 && (
            <div className="mt-1.5 text-xs font-semibold text-emerald-700">
              ₹{Number(customer.opportunity_value).toLocaleString("en-IN")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Pipeline() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [load]);

  const onDragStart = (e, customer) => {
    e.dataTransfer.setData("customer_id", customer.id);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(customer.id);
  };

  const onDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setHoverStage(stageId);
  };

  const onDragLeave = (stageId) => {
    setHoverStage((h) => (h === stageId ? null : h));
  };

  const onDrop = async (e, stageId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("customer_id");
    setDraggingId(null);
    setHoverStage(null);
    if (!id) return;
    const c = customers.find((x) => x.id === id);
    if (!c || c.status === stageId) return;
    // Optimistic update
    setCustomers((arr) =>
      arr.map((x) => (x.id === id ? { ...x, status: stageId } : x))
    );
    try {
      await api.put(`/customers/${id}`, { status: stageId });
      toast.success(`Moved to ${stageId}`);
    } catch {
      toast.error("Failed to move");
      load();
    }
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setHoverStage(null);
  };

  const groupedByStage = STAGES.reduce((acc, s) => {
    acc[s.id] = customers.filter((c) => c.status === s.id);
    return acc;
  }, {});

  return (
    <div data-testid="pipeline-page" onDragEnd={onDragEnd}>
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Pipeline
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Kanban Board
            </h1>
          </div>
          <div className="text-sm text-slate-500">
            Drag cards across stages to update their status
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-slate-500 text-center py-16">Loading pipeline…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {STAGES.map((stage) => {
              const cards = groupedByStage[stage.id] || [];
              const isHover = hoverStage === stage.id;
              return (
                <div
                  key={stage.id}
                  onDragOver={(e) => onDragOver(e, stage.id)}
                  onDragLeave={() => onDragLeave(stage.id)}
                  onDrop={(e) => onDrop(e, stage.id)}
                  className={`rounded-lg border ${
                    isHover
                      ? "border-blue-400 bg-blue-50/50 ring-2 ring-blue-100"
                      : "border-slate-200 bg-slate-50/60"
                  } transition-all`}
                  data-testid={`kanban-column-${stage.id}`}
                >
                  <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${stage.dot}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wider ${stage.text}`}>
                        {stage.label}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-full w-6 h-6 flex items-center justify-center">
                      {cards.length}
                    </span>
                  </div>
                  <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-220px)] overflow-y-auto">
                    {cards.length === 0 && (
                      <div className="text-center text-xs text-slate-400 py-6">
                        Drop here
                      </div>
                    )}
                    {cards.map((c) => (
                      <CustomerCard
                        key={c.id}
                        customer={c}
                        onDragStart={onDragStart}
                        dragging={draggingId === c.id}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
